import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useRoomCopy, roomError } from "@/locales/rooms";
import { toast } from "sonner";
import { MessageSquare, Plus, ArrowRight } from "lucide-react";

export default function DealRoomsPage() {
  const copy = useRoomCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const dealId = new URLSearchParams(location.search).get("deal") || "";
  const [rooms, setRooms] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showForm, setShowForm] = useState(Boolean(dealId));
  const [form, setForm] = useState({ title: "", side: "seller", deal_id: dealId });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    const [r, i] = await Promise.all([api.listRooms(), api.listRoomInvitations()]);
    setRooms(r); setInvitations(i);
  }, []);
  useEffect(() => {
    load().catch((e) => setError(roomError(e, copy)));
    api.listDeals().then((items) => {
      setDeals(items);
      const selected = items.find((d) => d.id === dealId);
      if (selected) setForm((old) => ({ ...old, title: selected.title }));
    }).catch(() => {});
    // Locale changes do not reset an unfinished room form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, dealId]);
  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const room = await api.createRoom({ ...form, deal_id: form.deal_id || null });
      navigate(`/app/rooms/${room.id}`);
    } catch (e) { setError(roomError(e, copy)); }
    finally { setBusy(false); }
  };
  const join = async (id) => {
    setBusy(true);
    try { const room = await api.acceptRoomInvitation(id); navigate(`/app/rooms/${room.id}`); }
    catch (e) { toast.error(roomError(e, copy)); await load().catch(() => {}); }
    finally { setBusy(false); }
  };
  return <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12" data-testid="deal-rooms">
    <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
      <div><div className="kicker mb-3">— TRADEDOCS</div><h1 className="font-serif text-4xl">{copy.title}</h1><p className="text-[var(--td-text-2)] mt-3 max-w-2xl">{copy.intro}</p></div>
      <button className="btn-primary inline-flex items-center gap-2" onClick={() => setShowForm(!showForm)}><Plus size={17} />{copy.create}</button>
    </div>
    {error && <p role="alert" className="p-4 mb-5 border border-red-500/30 text-[var(--td-text)]">{error}</p>}
    {showForm && <form onSubmit={create} className="card-surface p-5 sm:p-6 mb-8 space-y-4 max-w-3xl">
      <label className="block"><span className="td-label">{copy.roomTitle}</span><input required minLength={3} maxLength={240} className="td-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
      <div className="grid sm:grid-cols-2 gap-4"><label><span className="td-label">{copy.sourceDeal}</span><select className="td-input" value={form.deal_id} onChange={(e) => { const selected = deals.find((d) => d.id === e.target.value); setForm({ ...form, deal_id: e.target.value, title: selected?.title || form.title }); }}><option value="">{copy.standalone}</option>{deals.map((d) => <option key={d.id} value={d.id}>{d.reference ? `${d.reference} · ` : ""}{d.title}</option>)}</select></label>
      <label><span className="td-label">{copy.mySide}</span><select className="td-input" value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })}><option value="seller">{copy.seller}</option><option value="buyer">{copy.buyer}</option></select></label></div>
      <p className="text-sm text-[var(--td-text-3)]">{copy.shareHelp}</p><button disabled={busy} className="btn-primary disabled:opacity-40">{copy.create}</button>
    </form>}
    {invitations.length > 0 && <section className="mb-8"><h2 className="font-serif text-2xl mb-4">{copy.invitations}</h2><div className="grid md:grid-cols-2 gap-4">{invitations.map((i) => <div key={i.id} className="card-surface border-[var(--td-gold-muted)] p-5"><h3 className="font-serif text-xl">{i.title}</h3><p className="text-sm text-[var(--td-text-2)] my-2">{i.inviting_organisation} · {copy.expires} {new Date(i.expires_at).toLocaleDateString()}</p><button className="btn-primary mt-2" disabled={busy} onClick={() => join(i.id)}>{copy.join} {copy[i.side]}</button></div>)}</div></section>}
    <div className="grid lg:grid-cols-2 gap-4">{rooms.map((r) => <Link key={r.id} to={`/app/rooms/${r.id}`} className="card-surface p-5 sm:p-6 hover:border-[var(--td-gold-muted)] transition-colors"><div className="flex items-start gap-3"><MessageSquare className="text-[var(--td-gold)] shrink-0" size={22} /><div className="min-w-0 flex-1"><h2 className="font-serif text-2xl break-words">{r.title}</h2><div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-[var(--td-text-2)] mt-3">{r.participants.map((p) => <span key={p.side}>{copy[p.side]}: {p.name}</span>)}</div><p className="text-xs text-[var(--td-text-3)] mt-3">{copy[r.status]} · {copy.documents}: {r.document_count}</p></div><ArrowRight size={18} className="shrink-0" /></div></Link>)}</div>
    {!rooms.length && !error && <p className="text-[var(--td-text-3)] py-8">{copy.empty}</p>}
  </div>;
}

export function RoomInvitationPage() {
  const copy = useRoomCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const token = new URLSearchParams(location.hash.slice(1)).get("invite");
    if (!token) { setError(copy.unavailable); return; }
    let active = true;
    api.resolveRoomInvitation(token).then((i) => { if (active) setInvitation(i); }).catch(() => { if (active) setError(copy.unavailable); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);
  const join = async () => {
    setBusy(true);
    try { const room = await api.acceptRoomInvitation(invitation.id); navigate(`/app/rooms/${room.id}`, { replace: true }); }
    catch (e) { setError(roomError(e, copy)); }
    finally { setBusy(false); }
  };
  return <div className="p-6 md:p-12 max-w-3xl"><Link to="/app/rooms" className="text-sm text-[var(--td-gold)]">{copy.back}</Link><h1 className="font-serif text-3xl my-6">{copy.invitations}</h1>{error && <p role="alert">{error}</p>}{invitation && <div className="card-surface p-6"><h2 className="font-serif text-2xl">{invitation.title}</h2><p className="text-[var(--td-text-2)] my-3">{invitation.inviting_organisation}</p><p className="text-sm mb-5">{copy.inviteHelp}</p><button disabled={busy} onClick={join} className="btn-primary">{copy.join} {copy[invitation.side]}</button></div>}</div>;
}
